import React, { useEffect, useState, lazy, memo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const galleryImages = [{
  url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fgoYwb5qVRNFMWbn19NeNS/Restaurants_1.webp',
  title: 'Farm-to-Glass Experience',
  description: 'SmartSoda elevates the farm-to-table concept with premium beverage solutions that perfectly complement fresh, locally-sourced ingredients. Create an unforgettable culinary journey with drinks as thoughtfully crafted as your menu.'
}, {
  url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/d4ysWT2xobje81gueKuZ5a/Restaurants_2.webp',
  title: 'Health-Forward Hydration',
  description: "The perfect pairing for health-conscious dining establishments. SmartSoda's sleek system integrates seamlessly with fresh produce offerings, allowing guests to enjoy clean-label beverages that enhance their wellness-focused meal experience."
}, {
  url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hJrYze2FWPUW472Yy1Dtva/Restaurants_3.webp',
  title: 'Craft Beverage Revolution',
  description: 'Position your establishment at the forefront of the craft beverage movement. SmartSoda stands proudly alongside premium coffee equipment, offering patrons an elevated drinking experience that rivals your artisanal food offerings.'
}, {
  url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tx5ZsMknwpaVaW2Rxi3d1G/Restaurants_4.webp',
  title: 'Modern Minimalist Integration',
  description: "SmartSoda's sleek design complements contemporary restaurant aesthetics while delivering exceptional beverage options. The perfect balance of form and function for establishments that value both design and quality."
}, {
  url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eFny1YBX2jex8Bw2VhevZS/Restaurants_5.webp',
  title: 'Vibrant Flavor Innovation',
  description: "Transform ordinary dining into extraordinary experiences with SmartSoda's premium flavor options. Surrounded by fresh ingredients, our system empowers restaurants to create signature beverages that delight customers and boost profits."
}, {
  url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fi7D3Q73oAHEphd6NRWAHo/Restaurants_6.webp',
  title: 'Fast-Casual Revenue Booster',
  description: 'SmartSoda seamlessly integrates into fast-casual environments, providing premium beverage options that enhance customer experience and increase check averages. A perfect centerpiece that keeps customers coming back for both food and drinks.'
}, {
  url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pba9oDyb6WKWcWJhWfebQW/Restaurants_7.webp',
  title: 'Farm-to-Table Harmony',
  description: 'Perfect for artisanal and health-focused eateries, SmartSoda complements fresh, locally-sourced ingredients with clean-label beverages. Create a complete culinary story from appetizers to drinks that resonates with conscious consumers.'
}, {
  url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uaRRi8PKjW7WcczZZpELTc/Restaurants_8.webp',
  title: 'Family-Friendly Refreshment',
  description: 'SmartSoda brings families together with delicious, customizable beverage options that appeal to all ages. Boost loyalty and create memorable dining experiences with premium drinks that complement your family-friendly menu offerings.'
}];
const RestaurantImageGallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchPosition, setTouchPosition] = useState(null);
  // Auto-advance slides
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentIndex(prevIndex => prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1);
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAutoPlaying]);
  // Pause auto-play when user interacts
  const pauseAutoPlay = () => setIsAutoPlaying(false);
  const resumeAutoPlay = () => setIsAutoPlaying(true);
  // Navigation functions
  const goToPrevious = () => {
    pauseAutoPlay();
    setCurrentIndex(prevIndex => prevIndex === 0 ? galleryImages.length - 1 : prevIndex - 1);
    setTimeout(resumeAutoPlay, 10000);
  };
  const goToNext = () => {
    pauseAutoPlay();
    setCurrentIndex(prevIndex => prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1);
    setTimeout(resumeAutoPlay, 10000);
  };
  const goToSlide = slideIndex => {
    pauseAutoPlay();
    setCurrentIndex(slideIndex);
    setTimeout(resumeAutoPlay, 10000);
  };
  // Touch event handlers for mobile swipe
  const handleTouchStart = e => {
    const touchDown = e.touches[0].clientX;
    setTouchPosition(touchDown);
  };
  const handleTouchMove = e => {
    if (touchPosition === null) {
      return;
    }
    const currentTouch = e.touches[0].clientX;
    const diff = touchPosition - currentTouch;
    if (diff > 5) {
      goToNext();
    }
    if (diff < -5) {
      goToPrevious();
    }
    setTouchPosition(null);
  };
  return <section className="py-20 bg-gradient-to-b from-white via-blue-50 to-white" data-id="element-1166">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1167">
        <div className="text-center mb-12" data-id="element-1168">
          <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-blue-500 text-white shadow-sm mb-4" data-id="element-1169">
            SMARTSODA IN ACTION
          </span>
          <h2 className="text-3xl font-bold text-navy-950 sm:text-4xl mb-4" data-id="element-1170">
            Transforming Restaurant Experiences
          </h2>
          <p className="mt-2 text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-1171">
            See how SmartSoda's innovative beverage solutions are
            revolutionizing dining establishments across the country
          </p>
        </div>
        {/* Main Carousel - Side by Side Layout on desktop, stacked on mobile */}
        <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} data-id="element-1172">
          <div className="flex flex-col md:grid md:grid-cols-2 min-h-[700px] md:min-h-[650px]" data-id="element-1173">
            {/* Image Column - Always first on mobile */}
            <div className="bg-gray-100 flex items-center justify-center p-4 md:p-8 relative order-1 min-h-[350px]" data-id="element-1174">
              {galleryImages.map((image, index) => <div key={index} className={`absolute inset-0 p-4 md:p-8 transition-opacity duration-700 ease-in-out flex items-center justify-center ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-1175">
                  <div className="relative max-h-full w-auto" data-id="element-1176">
                    <img src={image.url} alt={image.title} className="max-h-[300px] md:max-h-[600px] w-auto object-contain mx-auto" style={{
                  maxWidth: '100%'
                }} loading={index === 0 ? 'eager' : 'lazy'} width="960" height="1088" data-id="element-1177" />
                  </div>
                </div>)}
            </div>
            {/* Text Column - Always second on mobile */}
            <div className="bg-gradient-to-br from-blue-50 to-white p-6 md:p-10 flex flex-col justify-center order-2" data-id="element-1178">
              {galleryImages.map((image, index) => <div key={index} className={`transition-opacity duration-700 ease-in-out ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0 absolute'}`} data-id="element-1179">
                  <h3 className="text-2xl md:text-3xl font-bold text-navy-950 mb-4" data-id="element-1180">
                    {image.title}
                  </h3>
                  <p className="text-lg text-gray-700 mb-8" data-id="element-1181">
                    {image.description}
                  </p>
                </div>)}
              {/* Navigation Controls */}
              <div className="mt-8 flex items-center justify-between" data-id="element-1182">
                <button onClick={goToPrevious} className="bg-white hover:bg-gray-100 text-gray-800 p-3 rounded-full shadow-md transition-all duration-300" aria-label="Previous image" data-id="element-1183">
                  <ChevronLeftIcon className="h-6 w-6" data-id="element-1184" />
                </button>
                <div className="flex space-x-2" data-id="element-1185">
                  {galleryImages.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-2 w-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-blue-500 w-6' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-1186" />)}
                </div>
                <button onClick={goToNext} className="bg-white hover:bg-gray-100 text-gray-800 p-3 rounded-full shadow-md transition-all duration-300" aria-label="Next image" data-id="element-1187">
                  <ChevronRightIcon className="h-6 w-6" data-id="element-1188" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default RestaurantImageGallery;